import React, { FC, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Dropdown, Drawer } from 'antd';
import { KeyOutlined } from '@ant-design/icons';
import Logo from './svg/Logo';
import treeImage from '../assets/images/background/TreeWithBird.png';
import HomeIcon from './svg/HomeButton';
import LogOutIcon from './svg/LogOut';
import ChangePasswordModal from './ChangePasswordModal';

interface AppHeaderProps {
  currentUser: {
    fullname: string;
    role: string;
    email: string;
  };
}

const AppHeader: FC<AppHeaderProps> = ({ currentUser }) => {
  const navigate = useNavigate();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/login';
  };

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const roleDisplay = currentUser.role.charAt(0).toUpperCase() + currentUser.role.slice(1).toLowerCase();

  return (
    <header className="sticky top-0 z-[100] pointer-events-auto px-5 md:px-[60px] pt-[45px] pb-[10px] flex justify-between items-center flex-row w-full">
      <Link 
        to="/" 
        className="logo-section cursor-pointer relative z-[101] pointer-events-auto block"
      >
        <Logo className="h-auto md:h-[46px] w-[140px] md:w-auto object-contain" />
      </Link>
      <div className="flex items-center justify-end w-auto gap-[12px] md:gap-5">
        <Link 
          to="/" 
          className="flex items-center gap-0 md:gap-[8px] bg-white p-[2.5px] md:p-[4px_16px_4px_4px] rounded-full shadow-[0px_4px_12px_rgba(0,0,0,0.08)] cursor-pointer transition-all duration-200 border-none hover:-translate-y-[2px] hover:shadow-[0px_6px_16px_rgba(0,0,0,0.12)] no-underline relative z-[101] pointer-events-auto"
        >
          <span className="flex items-center justify-center">
            <HomeIcon className="w-[30px] h-[30px] md:w-[28px] md:h-[28px]" />
          </span>
          <span className="hidden md:inline font-['Lexend',sans-serif] font-medium text-[14px] text-[#1F2A37]">Home</span>
        </Link>

        <div className="md:hidden w-[35px] h-[35px] bg-[#fbd38d] text-[#744210] flex items-center justify-center rounded-full font-semibold text-[13px] shadow-[0_4px_12px_rgba(0,0,0,0.08)] cursor-pointer hover:opacity-90 transition-opacity border-[3px] border-white box-border" onClick={() => setDrawerVisible(true)}>
          {getUserInitials(currentUser.fullname)}
        </div>

        <div className="hidden md:block">
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                {
                  key: '1',
                  label: (
                    <div className="flex flex-col py-1 pointer-events-none min-w-[200px]">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-['Lexend',sans-serif] text-[13px] font-semibold text-[#1F2A37]">{currentUser.fullname}</span>
                        <span className="bg-[#fef3c7] text-[#92400e] text-[10px] px-1.5 py-[2px] rounded flex items-center gap-1 font-medium font-['Lexend',sans-serif]">{roleDisplay}</span>
                      </div>
                      <span className="font-['Lexend',sans-serif] text-[11px] text-[#6b7280]">{currentUser.email}</span>
                    </div>
                  )
                },
                { type: 'divider' },
                {
                  key: 'change-password',
                  onClick: () => setChangePasswordVisible(true),
                  label: (
                    <div className="flex items-center justify-between w-full py-1 hover:text-[#4682B4] font-['Lexend',sans-serif] text-[12px] font-medium text-[#1F2A37] transition-colors">
                      <div className="flex items-center gap-2">
                        <KeyOutlined className="text-[#4682B4] text-[13px]" />
                        <span>Change Password</span>
                      </div>
                      <span className="text-[14px]">›</span>
                    </div>
                  )
                },
                { type: 'divider' },
                {
                  key: '2',
                  onClick: handleLogout,
                  label: (
                    <div className="flex items-center justify-between w-full py-1 hover:text-[#4682B4] font-['Lexend',sans-serif] text-[12px] font-medium text-[#1F2A37] transition-colors">
                      <div className="flex items-center gap-2">
                        <LogOutIcon className="w-[14px] h-[14px]" />
                        <span>Sign Out</span>
                      </div>
                      <span className="text-[14px]">›</span>
                    </div>
                  )
                }
              ]
            }}
            placement="bottomRight"
          >
            <div className="w-[38px] h-[38px] bg-[#fbd38d] text-[#744210] flex items-center justify-center rounded-full font-semibold text-sm shadow-[0_4px_12px_rgba(0,0,0,0.08)] cursor-pointer hover:opacity-90 transition-opacity border-[3px] border-white box-border">
              {getUserInitials(currentUser.fullname)}
            </div>
          </Dropdown>
        </div>
      </div>

      <Drawer
        placement="right"
        closable={false}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width="70%"
        styles={{ body: { padding: 0 } }}
        className="[&_.ant-drawer-content]:bg-white"
        zIndex={1000}
      >
        <div className="relative h-full w-full flex flex-col p-6 overflow-hidden">
          <div className="mb-6 mt-2">
            <button className="bg-transparent border-[1.2px] border-[#1F2A37] rounded-full w-[32px] h-[32px] flex items-center justify-center p-0 cursor-pointer hover:bg-[#1F2A37]/5 transition-colors" onClick={() => setDrawerVisible(false)}>
              <span className="text-[#1F2A37] font-medium text-[20px] leading-none mb-[3px]">‹</span>
            </button>
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-1">
              <span className="font-['Lexend',sans-serif] text-[16px] font-semibold text-[#1F2A37] pr-2 break-words">{currentUser.fullname}</span>
              <span className="bg-[#fef3c7] text-[#92400e] text-[11px] px-2 py-[4px] rounded flex items-center gap-1.5 font-medium font-['Lexend',sans-serif] shrink-0 border border-[#fbd38d]/50">
                <svg className="w-3 h-3 text-[#92400e]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                {roleDisplay}
              </span>
            </div>
            <span className="font-['Lexend',sans-serif] text-[13px] font-light text-[#6b7280]">{currentUser.email}</span>
          </div>

          <div className="w-full h-[1px] bg-[#f1f5f9] mb-4"></div>

          {/* Change Password */}
          <button className="flex items-center justify-between w-full bg-transparent border-none p-0 cursor-pointer group mb-4" onClick={() => { setDrawerVisible(false); setChangePasswordVisible(true); }}>
            <div className="flex items-center gap-3">
              <div className="bg-[#f1f5f9] p-[6px] rounded-[6px] flex items-center justify-center group-hover:bg-[#e2e8f0] transition-colors">
                <KeyOutlined className="text-[#4682B4] text-[14px]" />
              </div>
              <span className="font-['Lexend',sans-serif] text-[14px] font-medium text-[#1F2A37] group-hover:text-[#4682B4] transition-colors">Change Password</span>
            </div>
            <span className="text-[18px] text-[#475569] group-hover:text-[#4682B4] transition-colors">›</span>
          </button>

          <button className="flex items-center justify-between w-full bg-transparent border-none p-0 cursor-pointer group" onClick={handleLogout}>
            <div className="flex items-center gap-3">
              <div className="bg-[#f1f5f9] p-[6px] rounded-[6px] flex items-center justify-center group-hover:bg-[#e2e8f0] transition-colors">
                <LogOutIcon className="w-[18px] h-[18px] text-[#475569]" />
              </div>
              <span className="font-['Lexend',sans-serif] text-[14px] font-medium text-[#1F2A37] group-hover:text-[#4682B4] transition-colors">Sign Out</span>
            </div>
            <span className="text-[18px] text-[#475569] group-hover:text-[#4682B4] transition-colors">›</span>
          </button>

          {/* Bottom Tree Image */}
          <div className="absolute bottom-[0px] right-[0px] pointer-events-none z-0">
            <img src={treeImage} alt="" className="w-[200px] sm:w-[260px] h-auto object-contain origin-bottom-right" />
          </div>
        </div>
      </Drawer>

      {/* Change Password Modal */}
      <ChangePasswordModal
        open={changePasswordVisible}
        onCancel={() => setChangePasswordVisible(false)}
      />
    </header>
  );
};

export default AppHeader;